import React from "react";
import { Grid, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Input, Switch } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import '@progress/kendo-theme-fluent/dist/all.css';
import "./Grids.css";
import {
  FaPlusCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaFileExport,
  FaCar,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { Vehicle } from "../../data/interfaces";

interface VehiclesGridProps {
  data: {
    data: Vehicle[];
    total: number;
  };
  loading: boolean;
  dataState: {
    skip: number;
    take: number;
    filter?: any;
    sort?: any;
  };
  onDataStateChange: (event: GridDataStateChangeEvent) => void;
  onAddToggle: () => void;
  onEditToggle: (vehicleId: number) => void;
  onExport: () => void;
  /** When off (the default) vehicles with a date returned/sold are hidden */
  showReturnedSold: boolean;
  onShowReturnedSoldChange: (showReturnedSold: boolean) => void;
  /** False without edit access: no adding, rows open read-only */
  canEdit?: boolean;
}

// Custom text filter cell defined outside to prevent recreation on each render
const TextFilterCell = (props: any) => {
  const [value, setValue] = React.useState(props.value || '');

  React.useEffect(() => {
    setValue(props.value || '');
  }, [props.value]);

  const handleChange = (e: any) => {
    const newValue = e.value;
    setValue(newValue);
    
    if (props.onChange) {
      props.onChange({
        value: newValue,
        operator: newValue ? 'contains' : '',
        syntheticEvent: e.syntheticEvent,
      });
    }
  };

  return (
    <div className="k-filtercell">
      <Input
        value={value}
        onChange={handleChange}
      />
    </div>
  );
};

// Helper functions defined outside component
const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

const formatMileage = (mileage: number | null) => {
  if (!mileage) return "-";
  return mileage.toLocaleString();
};

// Custom cell components defined outside to prevent recreation on each render
const RegistrationCell = (props: any) => {
  return (
    <td style={{ fontWeight: "bold", color: "#0078d4" }}>
      {props.dataItem.registration}
    </td>
  );
};

const EmployeeCell = (props: any) => {
  return (
    <td>
      {props.dataItem.employeeName || "-"}
    </td>
  );
};

const TaxDateCell = (props: any) => {
  const isOverdue = props.dataItem.isTaxDue;
  return (
    <td style={{ color: isOverdue ? "#d13438" : "inherit" }}>
      {formatDate(props.dataItem.taxDate)}
      {isOverdue && " (OVERDUE)"}
    </td>
  );
};

const MOTDateCell = (props: any) => {
  const isOverdue = props.dataItem.isMOTDue;
  return (
    <td style={{ color: isOverdue ? "#d13438" : "inherit" }}>
      {formatDate(props.dataItem.motDate)}
      {isOverdue && " (OVERDUE)"}
    </td>
  );
};

const ServiceDateCell = (props: any) => {
  const serviceDate = props.dataItem.serviceDate;
  
  if (!serviceDate) {
    return <td>-</td>;
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const serviceDateObj = new Date(serviceDate);
  serviceDateObj.setHours(0, 0, 0, 0);
  
  // Calculate difference in milliseconds
  const diffTime = serviceDateObj.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // 4 weeks = 28 days
  const isOverdue = diffDays < 0;
  const isWithin4Weeks = diffDays >= 0 && diffDays <= 28;
  
  let color = "inherit";
  if (isOverdue) {
    color = "#d13438"; // red
  } else if (isWithin4Weeks) {
    color = "#f7630c"; // orange
  }
  
  return (
    <td style={{ color: color }}>
      {formatDate(serviceDate)}
      {isOverdue && " (OVERDUE)"}
    </td>
  );
};

const TrackerCell = (props: any) => {
  return (
    <td style={{ textAlign: "center" }}>
      {props.dataItem.hasTracker ? (
        <Tooltip anchorElement="target" position="top">
          <FaCheckCircle 
            style={{ color: "#107c10", fontSize: "16px" }} 
            title="Has Tracker"
          />
        </Tooltip>
      ) : (
        <Tooltip anchorElement="target" position="top">
          <FaTimesCircle 
            style={{ color: "#d13438", fontSize: "16px" }} 
            title="No Tracker"
          />
        </Tooltip>
      )}
    </td>
  );
};

const ServiceBookedCell = (props: any) => {
  return (
    <td style={{ textAlign: "center" }}>
      {props.dataItem.serviceBooked ? (
        <Tooltip anchorElement="target" position="top">
          <FaCheckCircle 
            style={{ color: "#107c10", fontSize: "16px" }} 
            title="Service Booked"
          />
        </Tooltip>
      ) : (
        <Tooltip anchorElement="target" position="top">
          <FaTimesCircle 
            style={{ color: "#d13438", fontSize: "16px" }} 
            title="No Service Booked"
          />
        </Tooltip>
      )}
    </td>
  );
};

const MOTBookedCell = (props: any) => {
  return (
    <td style={{ textAlign: "center" }}>
      {props.dataItem.motBooked ? (
        <Tooltip anchorElement="target" position="top">
          <FaCheckCircle 
            style={{ color: "#107c10", fontSize: "16px" }} 
            title="MOT Booked"
          />
        </Tooltip>
      ) : (
        <Tooltip anchorElement="target" position="top">
          <FaTimesCircle 
            style={{ color: "#d13438", fontSize: "16px" }} 
            title="No MOT Booked"
          />
        </Tooltip>
      )}
    </td>
  );
};

const MileageCell = (props: any) => {
  return (
    <td style={{ textAlign: "right" }}>
      {formatMileage(props.dataItem.mileage)}
    </td>
  );
};

const TotalCostsCell = (props: any) => {
  const totalCosts = props.dataItem.costsToDate;
  return (
    <td style={{ textAlign: "right", fontWeight: "500" }}>
      {totalCosts != null ? `£${totalCosts.toFixed(2)}` : '£0.00'}
    </td>
  );
};

const LeaseEndDateCell = (props: any) => {
  return (
    <td>
      {formatDate(props.dataItem.leaseEndDate)}
    </td>
  );
};

const DateReturnedSoldCell = (props: any) => {
  return (
    <td>
      {formatDate(props.dataItem.dateReturnedSold)}
    </td>
  );
};

const LeaseMonthlyPaymentCell = (props: any) => {
  const payment = props.dataItem.leaseMonthlyPayment;
  return (
    <td style={{ textAlign: "right" }}>
      {payment != null ? `£${payment.toFixed(2)}` : '-'}
    </td>
  );
};

const VehiclesGrid: React.FC<VehiclesGridProps> = (props) => {

  const exportToExcel = () => {
    const exportData = props.data.data.map((vehicle: Vehicle) => ({
      Registration: vehicle.registration,
      Make: vehicle.make || "",
      Model: vehicle.model || "",
      "Assigned Employee": vehicle.employeeName || "",
      "Tax Date": formatDate(vehicle.taxDate),
      "MOT Date": formatDate(vehicle.motDate),
      "MOT Booked": vehicle.motBooked ? "Yes" : "No",
      "Service Date": formatDate(vehicle.serviceDate),
      "Has Tracker": vehicle.hasTracker ? "Yes" : "No",
      "Service Booked": vehicle.serviceBooked ? "Yes" : "No",
      Mileage: vehicle.mileage || "",
      "Lease End": formatDate(vehicle.leaseEndDate),
      "Lease Payment": vehicle.leaseMonthlyPayment?.toFixed(2) || "",
      "Returned/Sold": formatDate(vehicle.dateReturnedSold),
      "Total Costs": vehicle.costsToDate?.toFixed(2) || "0.00",
      "Tax Due": vehicle.isTaxDue ? "Yes" : "No",
      "MOT Due": vehicle.isMOTDue ? "Yes" : "No",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    XLSX.writeFile(wb, "vehicles.xlsx");
  };

  return (
    <div className="vehicles-kendo-fluent-theme" style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 80px)',
      padding: '0 20px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #edebe9',
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaCar style={{ fontSize: '24px', color: '#1E2A3E' }} />
          <h2 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600',
            margin: 0,
            color: '#323130'
          }}>
            Vehicle Management
          </h2>
        </div>
        <p style={{
          fontSize: '14px',
          color: '#605e5c',
          margin: 0
        }}>
          Manage company vehicles, assignments, and maintenance schedules
        </p>
        {props.canEdit === false && (
          <p className='k-form-hint' style={{ margin: 0 }}>You have view-only access to vehicles.</p>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        marginBottom: "1rem",
        flexShrink: 0
      }}>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          {props.canEdit !== false && (
          <Button
            themeColor="primary"
            onClick={props.onAddToggle}
            style={{ backgroundColor: '#1E2A3E', borderColor: '#1E2A3E' }}
          >
            <FaPlusCircle style={{ marginRight: "0.5rem" }} />
            Add New Vehicle
          </Button>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Switch
              checked={props.showReturnedSold}
              onChange={(e) => props.onShowReturnedSoldChange(e.value)}
            />
            <span style={{ fontSize: "14px", color: "#323130" }}>
              Show returned/sold
            </span>
          </div>
        </div>
        
        <Button
          onClick={exportToExcel}
          fillMode="outline"
        >
          <FaFileExport style={{ marginRight: "0.5rem" }} />
          Export to Excel
        </Button>
      </div>

      {/* Kendo Grid */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Grid
          data={props.data}
          {...props.dataState}
          pageable={{
            buttonCount: 5,
            info: true,
            type: 'numeric',
            pageSizes: [10, 25, 50, 100],
            previousNext: true
          }}
          sortable={true}
          filterable={true}
          onDataStateChange={props.onDataStateChange}
          resizable={true}
          reorderable={true}
          style={{ 
            height: '100%'
          }}
          onRowDoubleClick={(e) => {
            props.onEditToggle(e.dataItem.vehicleId);
          }}
        >
        <GridColumn
          field="registration"
          title="Registration"
          width="140px"
          cell={RegistrationCell}
          filterCell={TextFilterCell}
        />
        <GridColumn
          field="make"
          title="Make"
          width="120px"
          filterCell={TextFilterCell}
        />
        <GridColumn
          field="model"
          title="Model"
          width="150px"
          filterCell={TextFilterCell}
        />
        <GridColumn
          field="employeeName"
          title="Assigned Employee"
          width="180px"
          cell={EmployeeCell}
          filterCell={TextFilterCell}
        />
        <GridColumn
          field="taxDate"
          title="Tax Due"
          width="140px"
          cell={TaxDateCell}
        />
        <GridColumn
          field="motDate"
          title="MOT Due"
          width="140px"
          cell={MOTDateCell}
        />
        <GridColumn
          field="motBooked"
          title="MOT Booked"
          width="120px"
          cell={MOTBookedCell}
          filterable={false}
        />
        <GridColumn
          field="serviceDate"
          title="Service Date"
          width="140px"
          cell={ServiceDateCell}
        />
        <GridColumn
          field="serviceBooked"
          title="Service Booked"
          width="120px"
          cell={ServiceBookedCell}
          filterable={false}
        />
        <GridColumn
          field="hasTracker"
          title="Tracker"
          width="80px"
          cell={TrackerCell}
          filterable={false}
        />
        <GridColumn
          field="mileage"
          title="Mileage"
          width="120px"
          cell={MileageCell}
        />
        <GridColumn
          field="leaseEndDate"
          title="Lease End"
          width="140px"
          cell={LeaseEndDateCell}
        />
        <GridColumn
          field="leaseMonthlyPayment"
          title="Lease Payment"
          width="140px"
          cell={LeaseMonthlyPaymentCell}
          filterable={false}
        />
        <GridColumn
          field="dateReturnedSold"
          title="Returned/Sold"
          width="140px"
          cell={DateReturnedSoldCell}
          filterable={false}
        />
        <GridColumn
          field="costsToDate"
          title="Total Costs"
          width="140px"
          cell={TotalCostsCell}
          filterable={false}
        />
      </Grid>
      </div>
    </div>
  );
};

export default VehiclesGrid;